// =============================================================================
// io/sync/bestOpps.ts — the `best_opps` table.
// -----------------------------------------------------------------------------
// ✅ A REAL TABLE, per-row upsert on `id` plus a row delete — NOT a jsonb
// collection. So D141's read-before-write hazard, which governs `customWidgets`
// and `prop_templates`, does NOT apply here: two edits from two tabs touch two
// rows and neither can revert the other.
//
// ⚠ TWO FOREIGN KEYS, both `ON DELETE SET NULL`:
//     best_opps.pattern_id -> patterns.id
//     best_opps.trade_id   -> trades.id
// So a deleted pattern or trade nulls the link rather than taking the opp with
// it, and a card whose pattern has since gone renders "(no pattern)". That is
// beta's behaviour and the column default already produces it.
//
// ⚠ `journal_date` IS STILL A COLUMN AND IS DELIBERATELY NOT WRITTEN. Beta's
// Phase 2 audit found it a write-only duplicate of `date` with zero readers and
// stopped writing it; existing rows keep whatever they have until a migration
// drops it. Writing it again would resurrect the duplicate.
// =============================================================================

import type { SupabaseClient } from '@supabase/supabase-js';
import { estRRToDb, type BestOpp } from '../../domain/bestOpps';
import { reportFailureAs } from '../diagnostics/errorLog';
import { PAGE_ROWS } from './load';

const COLUMNS = [
  'id', 'date', 'contract', 'time', 'pattern_id', 'grade', 'taken',
  'entry_zone', 'stop_zone', 'target_zone', 'est_rr', 'est_risk_dollar',
  'reason', 'would_do_different', 'trade_id', 'screenshots',
].join(', ');

function str(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

export function bestOppFromDb(r: Record<string, unknown>): BestOpp {
  return {
    id: str(r['id']),
    identity: {
      date: str(r['date']),
      contract: str(r['contract']),
      time: str(r['time']),
      patternId: str(r['pattern_id']),
      grade: str(r['grade']),
      taken: !!r['taken'],
    },
    context: {
      entryZone: str(r['entry_zone']),
      stopZone: str(r['stop_zone']),
      targetZone: str(r['target_zone']),
      // ⚠ NUMERIC in, free text out — the field is a text input and the card
      // stringifies it. `null` becomes '', which is what "not entered" means.
      estRR: r['est_rr'] == null ? '' : String(r['est_rr']),
      estRiskDollar: typeof r['est_risk_dollar'] === 'number' ? r['est_risk_dollar'] : 0,
    },
    reflection: {
      reason: str(r['reason']),
      wouldDoDifferent: str(r['would_do_different']),
    },
    linkage: { tradeId: str(r['trade_id']) },
    screenshots: Array.isArray(r['screenshots'])
      ? (r['screenshots'] as unknown[]).filter((s): s is string => typeof s === 'string')
      : [],
  };
}

/**
 * Every best opp the user has.
 *
 * ⚠ NO DATE OR ACCOUNT FILTER, and that is not an omission — the tab renders the
 * whole collection however the global filter is set. See `domain/bestOpps`.
 *
 * ⚠ An empty list and a failed read are NOT the same thing here either: this tab
 * has a friendly "No best opps logged yet" empty state, and a failed read that
 * fell through to it would tell a user their log was empty when it was merely
 * unreachable — on a screen whose entire content is user-authored and
 * unrecoverable if they then start again on top of it.
 */
export async function loadBestOpps(sb: SupabaseClient): Promise<BestOpp[]> {
  const { data, error } = await sb
    .from('best_opps')
    .select(COLUMNS)
    .order('date', { ascending: false });

  if (error) {
    throw new Error(
      `loadBestOpps: ${error.message}. Refusing to render an empty log — the tab's ` +
        `empty state says "No best opps logged yet", which would be a lie about ` +
        `content only the user can recreate.`,
    );
  }

  const rows = (data ?? []) as unknown as Record<string, unknown>[];
  if (rows.length >= PAGE_ROWS) {
    throw new Error(
      `loadBestOpps: got ${rows.length} rows, at or above the ${PAGE_ROWS}-row server cap — ` +
        `the log is being truncated and the oldest entries are silently missing.`,
    );
  }
  return rows.map(bestOppFromDb);
}

export type SaveBestOppResult =
  | { ok: true }
  | { ok: false; error: string };

/**
 * Write one opp.
 *
 * ⚠ REFUSES BEFORE IT WRITES when Est R:R is unparseable, rather than sending
 * NaN. Beta converts with a bare `Number()`, so "3.5-4" lands as null and the
 * trader's typing is discarded behind a success toast. `estRRToDb` is the single
 * place that decides; this just refuses to proceed on its verdict.
 */
export async function saveBestOpp(
  sb: SupabaseClient,
  userId: string,
  o: BestOpp,
): Promise<SaveBestOppResult> {
  const rr = estRRToDb(o.context.estRR);
  if (!rr.ok) return { ok: false, error: rr.reason };

  const row = {
    id: o.id,
    user_id: userId,
    // ⚠ `date` is NOT NULL in the schema. An empty one is a broken row, not a
    // blank field, so it is caught here rather than by a Postgres error the user
    // would see as "something went wrong".
    date: o.identity.date || null,
    contract: o.identity.contract || null,
    time: o.identity.time || null,
    // ⚠ '' would violate the FK — the empty option must become NULL, not a
    // pattern id of zero length.
    pattern_id: o.identity.patternId || null,
    grade: o.identity.grade || null,
    taken: !!o.identity.taken,
    entry_zone: o.context.entryZone || null,
    stop_zone: o.context.stopZone || null,
    target_zone: o.context.targetZone || null,
    est_rr: rr.value,
    est_risk_dollar: o.context.estRiskDollar ?? null,
    reason: o.reflection.reason || null,
    would_do_different: o.reflection.wouldDoDifferent || null,
    trade_id: o.linkage.tradeId || null,
    screenshots: o.screenshots,
    updated_at: new Date().toISOString(),
  };
  if (!row.date) {
    return { ok: false, error: 'A best opp needs a date — the row cannot be stored without one.' };
  }

  const { error } = await sb.from('best_opps').upsert(row, { onConflict: 'id' });
  // ⚠ The bare Supabase message is all the user gets on this screen, so it is
  // also what the log records — the scope is what says which write it came from.
  if (error) return { ok: false, error: reportFailureAs('save best opp', error, error.message) };
  return { ok: true };
}

export async function deleteBestOpp(sb: SupabaseClient, id: string): Promise<SaveBestOppResult> {
  const { error } = await sb.from('best_opps').delete().eq('id', id);
  if (error) return { ok: false, error: reportFailureAs('delete best opp', error, error.message) };
  return { ok: true };
}
